import { tx as translateInterfaceText } from '@/utils/i18n';
/**
 * Registry Centralizado de Documentação
 * Fonte única de verdade para toda a documentação da HostVSL
 * 
 * IMPORTANTE: Atualizar este arquivo sempre que:
 * - Adicionar/remover rotas
 * - Criar/modificar stores
 * - Adicionar componentes principais
 * - Modificar design tokens
 * 
 * COR PRINCIPAL: #1D4FFF (Azul Host)
 */

// ============================================
// ROTAS DA APLICAÇÃO
// ============================================
export interface RouteEntry {
  path: string;
  component: string;
  description: string;
  lastUpdated: string;
  category: 'core' | 'videos' | 'playlists' | 'metrics' | 'settings' | 'other';
}

export const routesRegistry: RouteEntry[] = [
  // Core
  { path: '/', component: 'MyVideosV2', get description() { return translateInterfaceText("Dashboard principal (vídeos)"); }, lastUpdated: '2026-01-04', category: 'core' },
  { path: '/dashboard', component: 'Redirect → /', get description() { return translateInterfaceText("Compatibilidade"); }, lastUpdated: '2026-01-04', category: 'core' },
  
  // Videos
  { path: '/meus-videos', component: 'MyVideos', get description() { return translateInterfaceText("Gestão com pastas"); }, lastUpdated: '2026-01-04', category: 'videos' },
  { path: '/new-video', component: 'NewVideo', get description() { return translateInterfaceText("Upload de vídeo"); }, lastUpdated: '2026-01-04', category: 'videos' },
  { path: '/video/:id', component: 'VideoDetails', get description() { return translateInterfaceText("Detalhes do vídeo"); }, lastUpdated: '2026-01-04', category: 'videos' },
  
  // Playlists
  { path: '/playlists', component: 'PlaylistList', get description() { return translateInterfaceText("Lista de playlists"); }, lastUpdated: '2026-01-04', category: 'playlists' },
  { path: '/playlists/:id/edit', component: 'PlaylistEditor', get description() { return translateInterfaceText("Editor de playlist"); }, lastUpdated: '2026-01-04', category: 'playlists' },
  { path: '/playlist/preview/:template', component: 'PlaylistPreview', get description() { return translateInterfaceText("Preview de template"); }, lastUpdated: '2026-01-04', category: 'playlists' },
  
  // Metrics
  { path: '/metrics', component: 'Metrics', get description() { return translateInterfaceText("Métricas detalhadas"); }, lastUpdated: '2026-01-04', category: 'metrics' },
  
  // Settings & Other
  { path: '/cloaker', component: 'VSLCloaker', get description() { return translateInterfaceText("Regras de cloaking"); }, lastUpdated: '2026-01-04', category: 'settings' },
  { path: '/vsl-dynamic/*', component: 'VSLDynamic*', get description() { return translateInterfaceText("Funil dinâmico"); }, lastUpdated: '2026-01-04', category: 'other' },
  { path: '/webinars/*', component: 'Webinar*', get description() { return translateInterfaceText("Sistema de webinars"); }, lastUpdated: '2026-01-04', category: 'other' },
  { path: '/profile', component: 'Profile', get description() { return translateInterfaceText("Perfil do usuário"); }, lastUpdated: '2026-01-04', category: 'settings' },
  { path: '/ab-test', component: 'ABTestListView', get description() { return translateInterfaceText("Lista de Testes A/B"); }, lastUpdated: '2026-01-04', category: 'other' },
  { path: '/ab-test/criar', component: 'ABTestWizard', get description() { return translateInterfaceText("Wizard criação Teste A/B"); }, lastUpdated: '2026-01-04', category: 'other' },
  { path: '/hook-track', component: 'HookTrackListView', get description() { return translateInterfaceText("Lista de Testes de Hook"); }, lastUpdated: '2026-01-04', category: 'other' },
  { path: '/hook-track/criar', component: 'HookTrackWizard', get description() { return translateInterfaceText("Wizard criação Teste Hook"); }, lastUpdated: '2026-01-04', category: 'other' },
  { path: '/documentacao', component: 'Documentation', get description() { return translateInterfaceText("Documentação técnica"); }, lastUpdated: '2026-01-04', category: 'other' },
  { path: '/partners', component: 'Partners', get description() { return translateInterfaceText("Parceiros oficiais"); }, lastUpdated: '2026-01-04', category: 'other' },
];

// ============================================
// ZUSTAND STORES
// ============================================
export interface StoreEntry {
  name: string;
  hookName: string;
  path: string;
  description: string;
  hasPersistence: boolean;
  lastUpdated: string;
  color: string;
}

export const storesRegistry: StoreEntry[] = [
  { 
    name: 'videoDashboard', 
    hookName: 'useVideoDashboardStore',
    path: 'src/store/videoDashboard/', 
    get description() { return translateInterfaceText("Vídeos & Pastas"); },
    hasPersistence: true,
    lastUpdated: '2026-01-04',
    color: 'emerald'
  },
  { 
    name: 'videoFeatures', 
    hookName: 'useVideoFeaturesStore',
    path: 'src/store/videoFeatures/', 
    get description() { return translateInterfaceText("Config. Player"); },
    hasPersistence: true,
    lastUpdated: '2026-01-04',
    color: 'violet'
  },
  { 
    name: 'playlist', 
    hookName: 'usePlaylistStore',
    path: 'src/store/playlist/', 
    get description() { return translateInterfaceText("Playlists"); },
    hasPersistence: true,
    lastUpdated: '2026-01-04',
    color: 'orange'
  },
  { 
    name: 'vsl-dinamica',
    hookName: 'useEditorStore',
    path: 'src/store/vsl-dinamica/',
    get description() { return translateInterfaceText("Editor da VSL Dinâmica (um store por editor)"); },
    hasPersistence: false,
    lastUpdated: '2026-10-07',
    color: 'pink'
  },
  { 
    name: 'webinar', 
    hookName: 'useWebinarStore',
    path: 'src/store/webinar/', 
    get description() { return translateInterfaceText("Webinars"); },
    hasPersistence: true,
    lastUpdated: '2026-01-04',
    color: 'cyan'
  },
  { 
    name: 'hierarchicalFolders', 
    hookName: 'useHierarchicalFoldersStore',
    path: 'src/store/hierarchicalFolders/', 
    get description() { return translateInterfaceText("Pastas Hierárq."); },
    hasPersistence: true,
    lastUpdated: '2026-01-04',
    color: 'amber'
  },
  { 
    name: 'abTest', 
    hookName: 'useABTestStore',
    path: 'src/store/abTest/', 
    get description() { return translateInterfaceText("Testes A/B"); },
    hasPersistence: false,
    lastUpdated: '2026-01-04',
    color: 'red'
  },
  { 
    name: 'abTestWizard', 
    hookName: 'useABTestWizardStore',
    path: 'src/store/ab-test/wizardStore.ts', 
    get description() { return translateInterfaceText("Wizard Teste A/B"); },
    hasPersistence: false,
    lastUpdated: '2026-01-04',
    color: 'blue'
  },
  { 
    name: 'hookTrackWizard', 
    hookName: 'useHookTrackWizardStore',
    path: 'src/store/hook-track/wizardStore.ts', 
    get description() { return translateInterfaceText("Wizard Teste Hook"); },
    hasPersistence: false,
    lastUpdated: '2026-01-04',
    color: 'teal'
  },
  { 
    name: 'sidebarPrefs', 
    hookName: 'useSidebarPrefsStore',
    path: 'src/store/sidebarPrefs/', 
    get description() { return translateInterfaceText("Preferências UI"); },
    hasPersistence: true,
    lastUpdated: '2026-01-04',
    color: 'slate'
  },
];

// ============================================
// COMPONENTES PRINCIPAIS (por domínio)
// ============================================
export interface ComponentEntry {
  name: string;
  path: string;
  description: string;
  lastUpdated: string;
}

export const componentsRegistry: Record<string, ComponentEntry[]> = {
  layout: [
    { name: 'Sidebar', path: 'src/components/layout/sidebar/', get description() { return translateInterfaceText("Navegação lateral"); }, lastUpdated: '2026-01-04' },
    { name: 'Header', path: 'src/components/layout/header/', get description() { return translateInterfaceText("Cabeçalho desktop"); }, lastUpdated: '2026-01-04' },
    { name: 'MobileHeader', path: 'src/components/layout/MobileHeader.tsx', get description() { return translateInterfaceText("Cabeçalho mobile"); }, lastUpdated: '2026-01-04' },
    { name: 'MobileMenu', path: 'src/components/layout/MobileMenu.tsx', get description() { return translateInterfaceText("Menu mobile"); }, lastUpdated: '2026-01-04' },
    { name: 'DrawerContent', path: 'src/components/layout/DrawerContent.tsx', get description() { return translateInterfaceText("Menu drawer mobile"); }, lastUpdated: '2026-01-04' },
    { name: 'Layout', path: 'src/components/layout/Layout.tsx', get description() { return translateInterfaceText("Layout principal"); }, lastUpdated: '2026-01-04' },
  ],
  partners: [
    { name: 'PartnerCard', path: 'src/components/partners/PartnerCard.tsx', get description() { return translateInterfaceText("Card de parceiro premium"); }, lastUpdated: '2026-01-04' },
    { name: 'PartnerCardSkeleton', path: 'src/components/partners/PartnerCardSkeleton.tsx', get description() { return translateInterfaceText("Skeleton loading de parceiro"); }, lastUpdated: '2026-01-04' },
  ],
  dashboard: [
    { name: 'StatsCard', path: 'src/components/dashboard/StatsCard.tsx', get description() { return translateInterfaceText("Card de métricas unificado"); }, lastUpdated: '2026-01-04' },
    { name: 'VideoCard', path: 'src/components/dashboard/VideoCard.tsx', get description() { return translateInterfaceText("Card de vídeo"); }, lastUpdated: '2026-01-04' },
    { name: 'WelcomeBanner', path: 'src/components/dashboard/WelcomeBanner.tsx', get description() { return translateInterfaceText("Banner hero"); }, lastUpdated: '2026-01-04' },
    { name: 'DashboardVideoFeed', path: 'src/components/dashboard/DashboardVideoFeed.tsx', get description() { return translateInterfaceText("Feed de vídeos"); }, lastUpdated: '2026-01-04' },
  ],
  metrics: [
    { name: 'MetricCard', path: 'src/components/metrics/', get description() { return translateInterfaceText("Cards de métricas"); }, lastUpdated: '2026-01-04' },
    { name: 'PrimeTimesGrid', path: 'src/components/metrics/', get description() { return translateInterfaceText("Heatmap de horários"); }, lastUpdated: '2026-01-04' },
    { name: 'TrafficRetentionChart', path: 'src/components/metrics/', get description() { return translateInterfaceText("Gráfico de retenção"); }, lastUpdated: '2026-01-04' },
    { name: 'ViewsChart', path: 'src/components/metrics/', get description() { return translateInterfaceText("Gráfico de visualizações"); }, lastUpdated: '2026-01-04' },
  ],
  playlist: [
    { name: 'PlaylistCard', path: 'src/components/playlist/', get description() { return translateInterfaceText("Card de playlist"); }, lastUpdated: '2026-01-04' },
    { name: 'PlaylistEditor', path: 'src/components/playlist/', get description() { return translateInterfaceText("Editor unificado"); }, lastUpdated: '2026-01-04' },
    { name: 'TemplateSelector', path: 'src/components/playlist/', get description() { return translateInterfaceText("Seletor de templates"); }, lastUpdated: '2026-01-04' },
  ],
  shared: [
    { name: 'VideoSelectorV2', path: 'src/components/shared/VideoSelectorV2.tsx', get description() { return translateInterfaceText("Seletor de vídeo premium"); }, lastUpdated: '2026-01-04' },
    { name: 'CalendarDateRangePicker', path: 'src/components/shared/', get description() { return translateInterfaceText("Seletor de período"); }, lastUpdated: '2026-01-04' },
  ],
  abTest: [
    { name: 'VideosSelectionStep', path: 'src/components/ab-test/wizard/steps/', get description() { return translateInterfaceText("Seleção de vídeos com badge ordem"); }, lastUpdated: '2026-01-04' },
    { name: 'ABTestTrafficDistributionStep', path: 'src/components/ab-test/wizard/steps/', get description() { return translateInterfaceText("Distribuição drag-and-drop"); }, lastUpdated: '2026-01-04' },
    { name: 'SortableABTestVideoCard', path: 'src/components/ab-test/wizard/steps/', get description() { return translateInterfaceText("Card sortável com slider"); }, lastUpdated: '2026-01-04' },
    { name: 'ABTestVideoCardOverlay', path: 'src/components/ab-test/wizard/steps/', get description() { return translateInterfaceText("Overlay drag visual"); }, lastUpdated: '2026-01-04' },
    { name: 'ABTestCard', path: 'src/components/ab-test/', get description() { return translateInterfaceText("Card de teste na lista"); }, lastUpdated: '2026-01-04' },
    { name: 'ABTestDetailsView', path: 'src/components/ab-test/', get description() { return translateInterfaceText("Visualização detalhada"); }, lastUpdated: '2026-01-04' },
  ],
  hookTrack: [
    { name: 'IntrosSelectionStep', path: 'src/components/hook-track/wizard/steps/', get description() { return translateInterfaceText("Seleção intros com badge ordem"); }, lastUpdated: '2026-01-04' },
    { name: 'TrafficDistributionStep', path: 'src/components/hook-track/wizard/steps/', get description() { return translateInterfaceText("Distribuição drag-and-drop"); }, lastUpdated: '2026-01-04' },
    { name: 'SortableHookTrackCard', path: 'src/components/hook-track/wizard/steps/', get description() { return translateInterfaceText("Card sortável com slider"); }, lastUpdated: '2026-01-04' },
    { name: 'HookTrackCardOverlay', path: 'src/components/hook-track/wizard/steps/', get description() { return translateInterfaceText("Overlay drag visual"); }, lastUpdated: '2026-01-04' },
    { name: 'HookTrackWizardFooter', path: 'src/components/hook-track/wizard/', get description() { return translateInterfaceText("Footer com navegação"); }, lastUpdated: '2026-01-04' },
    { name: 'HookTrackWizardStepper', path: 'src/components/hook-track/wizard/', get description() { return translateInterfaceText("Stepper visual etapas"); }, lastUpdated: '2026-01-04' },
    { name: 'HookTrackCard', path: 'src/components/hook-track/', get description() { return translateInterfaceText("Card de teste na lista"); }, lastUpdated: '2026-01-04' },
  ],
  featureConfigs: [
    { name: 'AutoplayStyleSection', path: 'src/components/feature-configs/autoplay/', get description() { return translateInterfaceText("Estilo avançado autoplay"); }, lastUpdated: '2026-01-04' },
    { name: 'VideoControlsTab', path: 'src/components/feature-configs/', get description() { return translateInterfaceText("Controles do player"); }, lastUpdated: '2026-01-04' },
    { name: 'ProgressBarConfig', path: 'src/components/feature-configs/', get description() { return translateInterfaceText("Barra de progresso"); }, lastUpdated: '2026-01-04' },
  ],
};

// ============================================
// DESIGN TOKENS
// ============================================
export const designTokens = {
  colors: {
    primary: { value: '#1D4FFF', get description() { return translateInterfaceText("Azul Host oficial - cor principal"); }, lastUpdated: '2026-01-04' },
    primaryLight: { value: '#4B7BFF', get description() { return translateInterfaceText("Azul Host claro - hover states"); }, lastUpdated: '2026-01-04' },
    primaryDark: { value: '#1640CC', get description() { return translateInterfaceText("Azul Host escuro - estados pressionados"); }, lastUpdated: '2026-01-04' },
    background: { value: '#FFFFFF', get description() { return translateInterfaceText("Fundo principal"); }, lastUpdated: '2026-01-04' },
    foreground: { value: '#0F172A', get description() { return translateInterfaceText("Texto principal"); }, lastUpdated: '2026-01-04' },
    muted: { value: '#64748B', get description() { return translateInterfaceText("Texto secundário"); }, lastUpdated: '2026-01-04' },
    border: { value: '#E8E8E8', get description() { return translateInterfaceText("Bordas padrão"); }, lastUpdated: '2026-01-04' },
    card: { value: '#FFFFFF', get description() { return translateInterfaceText("Fundo de cards"); }, lastUpdated: '2026-01-04' },
    switchCard: { value: '#F8FAFC', get description() { return translateInterfaceText("Fundo de switch cards"); }, lastUpdated: '2026-01-04' },
    success: { value: '#4CC38A', get description() { return translateInterfaceText("Verde ativo"); }, lastUpdated: '2026-01-04' },
    error: { value: '#E5484D', get description() { return translateInterfaceText("Vermelho erro/excluir"); }, lastUpdated: '2026-01-04' },
  },
  typography: {
    numbersMain: { weight: 500, size: '36px', get description() { return translateInterfaceText("Números principais em métricas"); }, lastUpdated: '2026-01-04' },
    tooltipTitle: { weight: 600, size: '14px', get description() { return translateInterfaceText("Títulos de tooltips"); }, lastUpdated: '2026-01-04' },
    tooltipValues: { weight: 400, size: '14px', get description() { return translateInterfaceText("Valores em tooltips"); }, lastUpdated: '2026-01-04' },
  },
  shadows: {
    card: { value: '0 4px 20px rgba(0,0,0,0.04)', get description() { return translateInterfaceText("Sombra padrão de cards"); }, lastUpdated: '2026-01-04' },
    tooltip: { value: '0px 14px 28px rgba(0,0,0,0.03)', get description() { return translateInterfaceText("Sombra de tooltips"); }, lastUpdated: '2026-01-04' },
    dropdown: { value: '0 8px 24px rgba(0,0,0,0.06)', get description() { return translateInterfaceText("Sombra de dropdowns"); }, lastUpdated: '2026-01-04' },
    drag: { value: '0 8px 32px rgba(29,79,255,0.18)', get description() { return translateInterfaceText("Sombra durante drag"); }, lastUpdated: '2026-01-04' },
  },
  borderRadius: {
    card: { value: '12px', get description() { return translateInterfaceText("Border-radius de cards"); }, lastUpdated: '2026-01-04' },
    button: { value: '10px', get description() { return translateInterfaceText("Border-radius de botões"); }, lastUpdated: '2026-01-04' },
    dropdown: { value: '10px', get description() { return translateInterfaceText("Border-radius de dropdowns"); }, lastUpdated: '2026-01-04' },
    switchCard: { value: '14px', get description() { return translateInterfaceText("Border-radius de switch cards"); }, lastUpdated: '2026-01-04' },
    modal: { value: '20px', get description() { return translateInterfaceText("Border-radius de modais"); }, lastUpdated: '2026-01-04' },
  },
};

// ============================================
// TECNOLOGIAS
// ============================================
export const techStack = [
  { name: 'React 18', get description() { return translateInterfaceText("UI Library"); }, lastUpdated: '2026-01-04' },
  { name: 'TypeScript', get description() { return translateInterfaceText("Type Safety"); }, lastUpdated: '2026-01-04' },
  { name: 'Vite', get description() { return translateInterfaceText("Build Tool"); }, lastUpdated: '2026-01-04' },
  { name: 'Tailwind CSS', get description() { return translateInterfaceText("Styling"); }, lastUpdated: '2026-01-04' },
  { name: 'Zustand', get description() { return translateInterfaceText("State Management"); }, lastUpdated: '2026-01-04' },
  { name: 'Supabase', get description() { return translateInterfaceText("Backend"); }, lastUpdated: '2026-01-04' },
  { name: 'React Query', get description() { return translateInterfaceText("Data Fetching"); }, lastUpdated: '2026-01-04' },
  { name: 'React Router', get description() { return translateInterfaceText("Routing"); }, lastUpdated: '2026-01-04' },
  { name: 'DnD-Kit', get description() { return translateInterfaceText("Drag and Drop"); }, lastUpdated: '2026-01-04' },
  { name: 'Radix UI', get description() { return translateInterfaceText("Primitives"); }, lastUpdated: '2026-01-04' },
];

// ============================================
// ESTRUTURA DE DIRETÓRIOS
// ============================================
export const directoryStructure = `src/
├── components/           # Componentes React
│   ├── layout/          # Header, Sidebar, Footer
│   ├── ui/              # Componentes UI (shadcn/ui)
│   ├── dashboard/       # Cards e métricas
│   ├── playlist/        # Templates e editor
│   ├── video/           # Player e controles
│   ├── metrics/         # Gráficos e análises
│   ├── webinar/         # Sistema de webinars
│   ├── feature-configs/ # Configuradores do player
│   ├── ab-test/         # Teste A/B
│   │   ├── wizard/      # Wizard de criação
│   │   │   └── steps/   # Etapas do wizard
│   │   ├── charts/      # Gráficos
│   │   └── metrics/     # Métricas
│   └── hook-track/      # Teste de Hook
│       ├── wizard/      # Wizard de criação
│       │   └── steps/   # Etapas do wizard
│       ├── details/     # Detalhes
│       └── metrics/     # Métricas
├── pages/               # Páginas da aplicação
├── store/               # Zustand stores
│   ├── videoDashboard/  # Videos & folders
│   ├── videoFeatures/   # Player config
│   ├── playlist/        # Playlists
│   ├── vsl-dinamica/    # Editor da VSL Dinâmica
│   ├── webinar/         # Webinars
│   ├── ab-test/         # Stores Teste A/B
│   └── hook-track/      # Stores Teste Hook
├── hooks/               # Custom hooks
├── contexts/            # React contexts
├── services/            # API services
├── utils/               # Utilitários
├── lib/                 # Bibliotecas
├── docs/                # Registries de documentação
└── types/               # TypeScript types`;
